import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import * as FlatModels from "@medsurf/flat-models";
import { ApiControlService } from "@medsurf/flat-services";

type SimpleUpload = { order: number, fileName: string, progress: number, status: 'uploading' | 'pending' | 'error' | 'success', size: number, error?: Error }  

@Component({
    selector: 'medsurf-design-input-upload',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upload.input.design.html',
    styleUrls: ['./upload.input.design.scss'],
    standalone: false
})
export class UploadInputDesign {
  @Input({ required: true }) name!: string;
  @Input() inputClass?: string;
  @Input() accept = "image/*,video/*,audio/*,application/pdf";
  @Input() maxFileSize = 1_500_000_000;
  @Output() uploadFinished = new EventEmitter<FlatModels.MediaEntityModels.MediaEntityType>();
    
  lastMedia?: FlatModels.MediaEntityModels.MediaEntityType;
  uploads = signal<Map<string, SimpleUpload>>(new Map())
  sortedUploads = computed(() => {
    return Array.from(this.uploads().values()).sort((a, b) => a.order - b.order);
  })

  dragover = signal(false)

  readonly FlatModels = FlatModels;

  constructor(public apiControlService: ApiControlService,
              public translate: TranslateService) {
  }

  onDropzoneDrop(event: DragEvent) {
    this.dragover.set(false)
    if (!event.dataTransfer?.files.length) return;
    const files = Array.from(event.dataTransfer.files);
    this.onUpload(files);
  }

  onDropzoneClick() {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = this.accept;
    input.addEventListener('change', () => {
      if (!input.files?.length) return;
      const files = Array.from(input.files);
      this.onUpload(files);
    })
    input.click();
  }

  async onUpload(files: File[]) {
    const newUploads = new Map(files.map((file, order) => {
      const simpleUpload: SimpleUpload = {
        fileName: file.name,
        progress: 0,
        status: 'pending',
        size: file.size,
        order
      }
      return [ file.name, simpleUpload ]
    }))

    this.uploads.set(new Map([...newUploads]))

    const createdMedia: FlatModels.MediaEntityModels.MediaEntityType[] = [];
    let hasError = false;
    
    for (const file of files) {
      const simpleUpload = this.uploads().get(file.name)
      if (!simpleUpload) continue

      simpleUpload.status = 'uploading'
      this.uploads.set(new Map([...this.uploads(), [file.name, simpleUpload]]))

      const media = await new Promise<FlatModels.MediaEntityModels.MediaEntityType>((resolve, reject) => {
        this.apiControlService.createMediaUpload({
          file,
          maxSize: this.maxFileSize,
          accept: this.accept,
          action: 'uploadMedia',
          onError: (error) => reject(error),
          onProgress: (progress) => {
            simpleUpload.progress = progress;
            this.uploads.set(new Map([...this.uploads(), [file.name, simpleUpload]]))
          },
          onSuccess: (media) => {
            simpleUpload.status = 'success';
            this.uploads.set(new Map([...this.uploads(), [file.name, simpleUpload]]))
            resolve(media)
          }
        });
      }).catch((error: Error) => {
        simpleUpload.status = 'error';
        simpleUpload.error = error;
        this.uploads.set(new Map([...this.uploads(), [file.name, simpleUpload]]))
        hasError = true;
      })

      if (media) {
        createdMedia.push(media);
      }
    }
    
    const lastMedia = createdMedia[createdMedia.length - 1];
    if (lastMedia && !hasError) {
      this.uploadFinished.emit(lastMedia);
    }
  }
}
